<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, ref, watch } from 'vue';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import {
  DATA_REGISTRY_FILTER_FIELDS,
  optionsForField,
} from '../utils/dataRegistryConsole';

const props = defineProps({
  show: { type: Boolean, default: false },
  tab: { type: String, required: true },
  modelValue: { type: Object, required: true },
  sources: { type: Array, default: () => [] },
  facets: { type: Object, default: () => ({}) },
  containsFields: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:modelValue', 'apply', 'clear', 'close']);

const draft = ref({ ...props.modelValue });
const customField = ref('');

watch(
  () => [props.show, props.modelValue],
  () => {
    if (props.show) {
      draft.value = {
        ...props.modelValue,
        contains: [...(props.modelValue.contains || [])],
      };
      customField.value = '';
    }
  },
  { immediate: true, deep: true }
);

const selectFields = computed(() =>
  DATA_REGISTRY_FILTER_FIELDS.filter(
    field => !field.tabs || field.tabs.includes(props.tab)
  )
);

const optionsFor = field =>
  field.key === 'sourceId'
    ? props.sources.map(source => ({
        value: String(source.id),
        label: source.name || source.key || `#${source.id}`,
      }))
    : optionsForField(field, props.facets);

const isSelected = key => (draft.value.contains || []).includes(key);

const toggleContains = key => {
  const current = draft.value.contains || [];
  draft.value = {
    ...draft.value,
    contains: current.includes(key)
      ? current.filter(item => item !== key)
      : [...current, key],
  };
};

const addCustomField = () => {
  const key = customField.value.trim().toLowerCase();
  if (!key || isSelected(key)) return;

  draft.value = { ...draft.value, contains: [...(draft.value.contains || []), key] };
  customField.value = '';
};

const selectedCount = computed(() => (draft.value.contains || []).length);

const apply = () => {
  emit('update:modelValue', { ...draft.value });
  emit('apply');
  emit('close');
};

const clear = () => {
  emit('clear');
  emit('close');
};
</script>

<template>
  <LaisModal
    :show="show"
    title="Filtros avançados"
    max-width-class="max-w-[760px]"
    @close="emit('close')"
  >
    <div
      class="flex flex-col gap-5"
      data-test="data-registry-advanced-filters"
    >
      <section class="flex flex-col gap-3">
        <header class="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3
              class="font-redHatDisplay text-sm font-semibold text-lais-text"
            >
              O registro contém
            </h3>
            <p class="text-xs text-lais-text-muted">
              Filtra pelos campos realmente presentes no dado pessoal
              armazenado.
            </p>
          </div>
          <span
            class="rounded-full bg-lais-primary-50 px-2 py-1 text-xs font-semibold text-lais-primary"
          >
            {{ selectedCount }} selecionado(s)
          </span>
        </header>

        <div class="flex flex-wrap gap-2">
          <button
            v-for="field in containsFields"
            :key="field.key"
            type="button"
            class="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition"
            :class="
              isSelected(field.key)
                ? 'border-lais-primary bg-lais-primary-50 text-lais-primary'
                : 'border-lais-border bg-white text-lais-text-body hover:border-lais-primary'
            "
            :aria-pressed="isSelected(field.key)"
            :data-test="`data-registry-contains-${field.key}`"
            @click="toggleContains(field.key)"
          >
            <span class="size-4" :class="field.icon" aria-hidden="true" />
            {{ field.label }}
          </button>
        </div>

        <div class="flex flex-wrap items-end gap-3">
          <label
            class="flex min-w-[240px] flex-1 flex-col gap-1 text-xs font-semibold text-lais-text"
          >
            Outro campo (chave exata)
            <div class="flex gap-2">
              <input
                v-model="customField"
                class="min-h-9 flex-1 rounded-lais border border-lais-border px-3 text-sm font-normal"
                placeholder="ex.: pis, titulo_eleitor"
                data-test="data-registry-contains-custom"
                @keyup.enter="addCustomField"
              />
              <LaisPrimaryButton
                icon="i-lucide-plus"
                variant="ghost"
                size="sm"
                @click="addCustomField"
              >
                Adicionar
              </LaisPrimaryButton>
            </div>
          </label>

          <label
            class="flex flex-col gap-1 text-xs font-semibold text-lais-text"
          >
            Combinação
            <select
              v-model="draft.containsMode"
              class="min-h-9 rounded-lais border border-lais-border px-3 text-sm font-normal"
              data-test="data-registry-contains-mode"
            >
              <option value="all">Contém todos</option>
              <option value="any">Contém qualquer um</option>
            </select>
          </label>

          <label
            class="flex items-center gap-2 text-xs font-semibold text-lais-text"
          >
            <input
              v-model="draft.containsDeep"
              type="checkbox"
              class="size-4 rounded border-lais-border"
              data-test="data-registry-contains-deep"
            />
            Incluir campos originais da fonte
          </label>
        </div>
      </section>

      <section class="flex flex-col gap-3 border-t border-lais-border-soft pt-4">
        <h3 class="font-redHatDisplay text-sm font-semibold text-lais-text">
          Classificação
        </h3>
        <div class="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          <label
            v-for="field in selectFields"
            :key="field.key"
            class="flex min-w-0 flex-col gap-1 text-xs font-semibold text-lais-text"
          >
            {{ field.label }}
            <select
              v-model="draft[field.key]"
              class="min-h-9 rounded-lais border border-lais-border px-3 text-sm font-normal"
              :data-test="`data-registry-advanced-${field.key}`"
            >
              <option value="">{{ field.emptyLabel || 'Todos' }}</option>
              <option
                v-for="option in optionsFor(field)"
                :key="`${field.key}-${option.value}`"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </label>

          <label
            class="flex min-w-0 flex-col gap-1 text-xs font-semibold text-lais-text"
          >
            Chave manual da fonte
            <input
              v-model="draft.sourceKey"
              class="min-h-9 rounded-lais border border-lais-border px-3 text-sm font-normal"
              placeholder="source_key"
            />
          </label>
        </div>
      </section>
    </div>

    <template #footer>
      <div class="flex flex-wrap items-center justify-between gap-2">
        <LaisPrimaryButton
          icon="i-lucide-eraser"
          variant="ghost"
          size="sm"
          data-test="data-registry-advanced-clear"
          @click="clear"
        >
          Limpar tudo
        </LaisPrimaryButton>
        <div class="flex gap-2">
          <LaisPrimaryButton variant="ghost" size="sm" @click="emit('close')">
            Cancelar
          </LaisPrimaryButton>
          <LaisPrimaryButton
            icon="i-lucide-filter"
            size="sm"
            data-test="data-registry-advanced-apply"
            @click="apply"
          >
            Aplicar filtros
          </LaisPrimaryButton>
        </div>
      </div>
    </template>
  </LaisModal>
</template>
